// mds-dojo — dijit/_CssStateMixin — FAITHFUL port of references/dijit/_CssStateMixin.js.
// Codemod pipeline; declare from the faithful runtime. Adaptations for this package:
//  - `dojo/domReady` → `dojo/ready` (mds has no separate domReady module; ready(fn)
//    fires the document-level listener setup once the DOM is ready — same effect).
//  - `dijit/a11yclick` is not a separate module here, so its `press`/`release`
//    synthetic events are inlined below (byte-faithful to a11yclick.js's press/release).
//  - mds `dojo/on` does not treat a function `type` as an extension event, so the
//    extension events (touch.over/out, a11yclick.press/release) are INVOKED directly
//    as `ext(node, listener)` instead of `on(node, ext, listener)` — identical result.
//  - unused `dojo/has` import dropped.
// Mixin for widgets to set CSS classes on the widget DOM nodes depending on hover/
// mouse press/focus state changes, and higher-level state (disabled, selected, ...).
import array from '../dojo/_base/array';
import declare from '../dojo/_base/declare-runtime';
import dom from '../dojo/dom'; // dom.isDescendant()
import domClass from '../dojo/dom-class'; // domClass.toggle
import keys from '../dojo/keys'; // keys.ENTER keys.SPACE (a11yclick inline)
import lang from '../dojo/_base/lang'; // lang.hitch
import mouse from '../dojo/mouse'; // mouse.isLeft (a11yclick inline)
import on from '../dojo/on';
import ready from '../dojo/ready';
import touch from '../dojo/touch';
import win from '../dojo/_base/window'; // win.body
import registry from './registry';

// Inlined dijit/a11yclick press/release synthetic events (a11yclick is not a
// standalone module in this package). Faithful to references/dijit/a11yclick.js.
var a11yclick: any = {
	press: function (node: any, listener: any) {
		var touchListener = touch.press(node, function (evt: any) {
			if (evt.type == "mousedown" && !mouse.isLeft(evt)) {
				// Ignore right click
				return;
			}
			listener(evt);
		}), keyListener = on(node, "keydown", function (evt: any) {
			if (evt.keyCode === keys.ENTER || evt.keyCode === keys.SPACE) {
				listener(evt);
			}
		});
		return {
			remove: function () {
				touchListener.remove();
				keyListener.remove();
			}
		};
	},

	release: function (node: any, listener: any) {
		var touchListener = touch.release(node, function (evt: any) {
			if (evt.type == "mouseup" && !mouse.isLeft(evt)) {
				// Ignore right click
				return;
			}
			listener(evt);
		}), keyListener = on(node, "keyup", function (evt: any) {
			if (evt.keyCode === keys.ENTER || evt.keyCode === keys.SPACE) {
				listener(evt);
			}
		});
		return {
			remove: function () {
				touchListener.remove();
				keyListener.remove();
			}
		};
	}
};

var CssStateMixin: any = declare("dijit._CssStateMixin", [], {
	// summary:
	//		Mixin for widgets to set CSS classes on the widget DOM nodes depending on hover/mouse press/focus
	//		state changes, and also higher-level state changes such becoming disabled or selected.

	// hovering: [readonly] Boolean
	//		True if cursor is over this widget
	hovering: false,

	// active: [readonly] Boolean
	//		True if mouse was pressed while over this widget, and hasn't been released yet
	active: false,

	_applyAttributes: function () {
		// This code would typically be in postCreate(), but putting in _applyAttributes() for
		// performance: so the class changes happen before DOM is inserted into the document.
		// Change back to postCreate() in 2.0.  See #11635.

		this.inherited(arguments);

		// Monitoring changes to disabled, readonly, etc. state, and update CSS class of root node
		array.forEach(["disabled", "readOnly", "checked", "selected", "focused", "state", "hovering", "active", "_opened"], function (this: any, attr: string) {
			this.watch(attr, lang.hitch(this, "_setStateClass"));
		}, this);

		// Track hover and active mouse events on widget root node, plus possibly on subnodes
		for (var ap in this.cssStateNodes || {}) {
			this._trackMouseState(this[ap], this.cssStateNodes[ap]);
		}
		this._trackMouseState(this.domNode, this.baseClass);

		// Set state initially; there's probably no hover/active/focus state but widget might be
		// disabled/readonly/checked/selected so we want to set CSS classes for those conditions.
		this._setStateClass();
	},

	_cssMouseEvent: function (/*Event*/ event: any) {
		// summary:
		//		Handler for CSS event on this.domNode. Sets hovering and active properties depending on mouse state,
		//		which triggers _setStateClass() to set appropriate CSS classes for this.domNode.

		if (!this.disabled) {
			switch (event.type) {
				case "mouseover":
				case "MSPointerOver":
				case "pointerover":
					this._set("hovering", true);
					this._set("active", this._mouseDown);
					break;
				case "mouseout":
				case "MSPointerOut":
				case "pointerout":
					this._set("hovering", false);
					this._set("active", false);
					break;
				case "mousedown":
				case "touchstart":
				case "MSPointerDown":
				case "pointerdown":
				case "keydown":
					this._set("active", true);
					break;
				case "mouseup":
				case "dojotouchend":
				case "MSPointerUp":
				case "pointerup":
				case "keyup":
					this._set("active", false);
					break;
			}
		}
	},

	_setStateClass: function () {
		// summary:
		//		Update the visual state of the widget by setting the css classes on this.domNode
		//		(or this.stateNode if defined) by combining this.baseClass with
		//		various suffixes that represent the current widget state(s).

		// Compute new set of classes
		var newStateClasses = this.baseClass.split(" ");

		function multiply(modifier: string) {
			newStateClasses = newStateClasses.concat(array.map(newStateClasses, function (c: string) {
				return c + modifier;
			}), "dijit" + modifier);
		}

		if (!this.isLeftToRight()) {
			// For RTL mode we need to set an addition class like dijitTextBoxRtl.
			multiply("Rtl");
		}

		var checkedState = this.checked == "mixed" ? "Mixed" : (this.checked ? "Checked" : "");
		if (this.checked) {
			multiply(checkedState);
		}
		if (this.state) {
			multiply(this.state);
		}
		if (this.selected) {
			multiply("Selected");
		}
		if (this._opened) {
			multiply("Opened");
		}

		if (this.disabled) {
			multiply("Disabled");
		} else if (this.readOnly) {
			multiply("ReadOnly");
		} else {
			if (this.active) {
				multiply("Active");
			} else if (this.hovering) {
				multiply("Hover");
			}
		}

		if (this.focused) {
			multiply("Focused");
		}

		// Remove old state classes and add new ones.
		// For performance concerns we only write into domNode.className once.
		var tn = this.stateNode || this.domNode,
			classHash: any = {};	// set of all classes (state and otherwise) for node

		array.forEach(tn.className.split(" "), function (c: string) {
			classHash[c] = true;
		});

		if ("_stateClasses" in this) {
			array.forEach(this._stateClasses, function (c: string) {
				delete classHash[c];
			});
		}

		array.forEach(newStateClasses, function (c: string) {
			classHash[c] = true;
		});

		var newClasses = [];
		for (var c in classHash) {
			newClasses.push(c);
		}
		tn.className = newClasses.join(" ");

		this._stateClasses = newStateClasses;
	},

	_subnodeCssMouseEvent: function (node: any, clazz: string, evt: any) {
		// summary:
		//		Handler for hover/active mouse event on widget's subnode
		if (this.disabled || this.readOnly) {
			return;
		}

		function hover(isHovering: boolean) {
			domClass.toggle(node, clazz + "Hover", isHovering);
		}

		function active(isActive: boolean) {
			domClass.toggle(node, clazz + "Active", isActive);
		}

		function focused(isFocused: boolean) {
			domClass.toggle(node, clazz + "Focused", isFocused);
		}

		switch (evt.type) {
			case "mouseover":
			case "MSPointerOver":
			case "pointerover":
				hover(true);
				break;
			case "mouseout":
			case "MSPointerOut":
			case "pointerout":
				hover(false);
				active(false);
				break;
			case "mousedown":
			case "touchstart":
			case "MSPointerDown":
			case "pointerdown":
			case "keydown":
				active(true);
				break;
			case "mouseup":
			case "MSPointerUp":
			case "pointerup":
			case "dojotouchend":
			case "keyup":
				active(false);
				break;
			case "focus":
			case "focusin":
				focused(true);
				break;
			case "blur":
			case "focusout":
				focused(false);
				break;
		}
	},

	_trackMouseState: function (/*DomNode*/ node: any, /*String*/ clazz: string) {
		// summary:
		//		Track mouse/focus events on specified node and set CSS class on that node to indicate
		//		current state.   Usually not called directly, but via cssStateNodes attribute.

		// Flag for listener code below to call this._cssMouseEvent() or this._subnodeCssMouseEvent()
		// when node is hovered/active
		node._cssState = clazz;
	}
});

ready(function () {
	// Document level listener to catch hover etc. events on widget root nodes and subnodes.

	function pointerHandler(evt: any, target: any, relatedTarget?: any) {
		// Handler for mouseover, mouseout, a11yclick.press and a11click.release events

		// Poor man's event propagation.  Don't propagate event to ancestors of evt.relatedTarget,
		// to avoid processing mouseout events moving from a widget's domNode to a descendant node;
		// such events shouldn't be interpreted as a mouseleave on the widget.
		if (relatedTarget && dom.isDescendant(relatedTarget, target)) {
			return;
		}

		for (var node = target; node && node != relatedTarget; node = node.parentNode) {
			// Process any nodes with _cssState property.   They are generally widget root nodes,
			// but could also be sub-nodes within a widget
			if (node._cssState) {
				var widget = registry.getEnclosingWidget(node);
				if (widget) {
					if (node == widget.domNode) {
						// event on the widget's root node
						widget._cssMouseEvent(evt);
					} else {
						// event on widget's sub-node
						widget._subnodeCssMouseEvent(node, node._cssState, evt);
					}
				}
			}
		}
	}

	var body = win.body(), activeNode: any;

	// Handle pointer related events (i.e. mouse or touch)
	touch.over(body, function (evt: any) {
		// Using touch.over rather than mouseover mainly to ignore phantom mouse events on iOS.
		pointerHandler(evt, evt.target, evt.relatedTarget);
	});
	touch.out(body, function (evt: any) {
		// Using touch.out rather than mouseout mainly to ignore phantom mouse events on iOS.
		pointerHandler(evt, evt.target, evt.relatedTarget);
	});
	a11yclick.press(body, function (evt: any) {
		// Save the a11yclick.press target to reference when the a11yclick.release comes.
		activeNode = evt.target;
		pointerHandler(evt, activeNode);
	});
	a11yclick.release(body, function (evt: any) {
		// The release event could come on a separate node than the press event, if for example user slid finger.
		// Reference activeNode to reset the state of the node that got state set in the a11yclick.press handler.
		pointerHandler(evt, activeNode);
		activeNode = null;
	});

	// Track focus events on widget sub-nodes that have been registered via _trackMouseState().
	// Remove for 2.0 (if focus CSS needed, just use :focus pseudo-selector).
	on(body, "focusin, focusout", function (evt: any) {
		var node = evt.target;
		if (node._cssState && !node.getAttribute("widgetId")) {
			var widget = registry.getEnclosingWidget(node);
			if (widget) {
				widget._subnodeCssMouseEvent(node, node._cssState, evt);
			}
		}
	});
});

export default CssStateMixin;
